/**
 * 关联网络分析引擎（纯函数，可测试）。
 * 提供情报分析常用的图度量：度中心性、介数中心性、社群发现、
 * 割点/桥、最短路径、关联矩阵与关键节点排序。
 */
import type { Entity, Relation } from '../state/domain';

export type GNode = {
  id: string;
  label: string;
  type: string;
  targetId: string | null;
  status: string;
  confidence: number;
};

export type GEdge = {
  id: string;
  source: string;
  target: string;
  type: string;
  label: string | null;
  strength: number;
  confidence: number;
  status: string;
};

export type GraphModel = {
  nodes: GNode[];
  edges: GEdge[];
  index: Map<string, number>;
  adjacency: number[][];
};

export function buildGraphModel(entities: Entity[], relations: Relation[]): GraphModel {
  const nodes: GNode[] = entities.map(entity => ({
    id: entity.id,
    label: entity.value ?? entity.label ?? entity.type,
    type: entity.type,
    targetId: entity.targetId,
    status: entity.status,
    confidence: entity.confidence,
  }));
  const index = new Map<string, number>();
  nodes.forEach((node, i) => index.set(node.id, i));

  const edges: GEdge[] = [];
  const adjacency: number[][] = nodes.map(() => []);
  for (const relation of relations) {
    const a = index.get(relation.sourceId);
    const b = index.get(relation.targetId);
    if (a === undefined || b === undefined || a === b) continue;
    edges.push({
      id: relation.id,
      source: relation.sourceId,
      target: relation.targetId,
      type: relation.type,
      label: relation.label,
      strength: relation.strength,
      confidence: relation.confidence,
      status: relation.status,
    });
    adjacency[a].push(b);
    if (relation.direction !== 'forward') adjacency[b].push(a);
    else adjacency[b].push(a); // 情报关联默认按无向处理，便于社群与中心度分析
  }
  return { nodes, edges, index, adjacency };
}

/** 度中心性：连接数、加权度（关系强度之和）、入/出度。 */
export function degreeStats(graph: GraphModel): Map<string, { degree: number; weighted: number; inDegree: number; outDegree: number }> {
  const out = new Map<string, { degree: number; weighted: number; inDegree: number; outDegree: number }>();
  for (const node of graph.nodes) out.set(node.id, { degree: 0, weighted: 0, inDegree: 0, outDegree: 0 });
  for (const edge of graph.edges) {
    const a = out.get(edge.source);
    const b = out.get(edge.target);
    if (a) {
      a.degree += 1;
      a.weighted += edge.strength;
      a.outDegree += 1;
    }
    if (b) {
      b.degree += 1;
      b.weighted += edge.strength;
      b.inDegree += 1;
    }
  }
  return out;
}

/** 连通分量（弱连通）。 */
export function connectedComponents(graph: GraphModel): number[][] {
  const n = graph.nodes.length;
  const seen = new Array<boolean>(n).fill(false);
  const components: number[][] = [];
  for (let start = 0; start < n; start++) {
    if (seen[start]) continue;
    const stack = [start];
    const comp: number[] = [];
    seen[start] = true;
    while (stack.length) {
      const v = stack.pop() as number;
      comp.push(v);
      for (const w of graph.adjacency[v]) {
        if (!seen[w]) {
          seen[w] = true;
          stack.push(w);
        }
      }
    }
    components.push(comp);
  }
  return components;
}

/** 介数中心性（Brandes，无向无权）。 */
export function betweennessCentrality(graph: GraphModel): Map<string, number> {
  const n = graph.nodes.length;
  const cb = new Array<number>(n).fill(0);
  const adjacency = graph.adjacency;

  for (let s = 0; s < n; s++) {
    const stack: number[] = [];
    const pred: number[][] = Array.from({ length: n }, () => []);
    const sigma = new Array<number>(n).fill(0);
    const dist = new Array<number>(n).fill(-1);
    sigma[s] = 1;
    dist[s] = 0;
    const queue: number[] = [s];
    while (queue.length) {
      const v = queue.shift() as number;
      stack.push(v);
      for (const w of adjacency[v]) {
        if (dist[w] < 0) {
          dist[w] = dist[v] + 1;
          queue.push(w);
        }
        if (dist[w] === dist[v] + 1) {
          sigma[w] += sigma[v];
          pred[w].push(v);
        }
      }
    }
    const delta = new Array<number>(n).fill(0);
    while (stack.length) {
      const w = stack.pop() as number;
      for (const v of pred[w]) {
        delta[v] += (sigma[v] / sigma[w]) * (1 + delta[w]);
      }
      if (w !== s) cb[w] += delta[w];
    }
  }

  const result = new Map<string, number>();
  graph.nodes.forEach((node, i) => result.set(node.id, cb[i] / 2));
  return result;
}

/** 割点（Tarjan）：去掉后网络会分裂的关键节点。 */
export function articulationPoints(graph: GraphModel): string[] {
  const n = graph.nodes.length;
  const disc = new Array<number>(n).fill(-1);
  const low = new Array<number>(n).fill(0);
  const isCut = new Array<boolean>(n).fill(false);
  let timer = 0;

  const dfs = (u: number, parent: number): void => {
    disc[u] = low[u] = timer++;
    let children = 0;
    for (const v of graph.adjacency[u]) {
      if (disc[v] < 0) {
        children += 1;
        dfs(v, u);
        low[u] = Math.min(low[u], low[v]);
        if (parent !== -1 && low[v] >= disc[u]) isCut[u] = true;
      } else if (v !== parent) {
        low[u] = Math.min(low[u], disc[v]);
      }
    }
    if (parent === -1 && children > 1) isCut[u] = true;
  };

  for (let i = 0; i < n; i++) if (disc[i] < 0) dfs(i, -1);
  return graph.nodes.filter((_, i) => isCut[i]).map(node => node.id);
}

/** 桥（割边）：去掉后网络分裂的边。 */
export function bridgeEdges(graph: GraphModel): GEdge[] {
  const n = graph.nodes.length;
  const disc = new Array<number>(n).fill(-1);
  const low = new Array<number>(n).fill(0);
  let timer = 0;
  const bridges = new Set<string>();

  const edgeKey = (a: number, b: number) => [a, b].sort((x, y) => x - y).join('|');

  const dfs = (u: number, parent: number): void => {
    disc[u] = low[u] = timer++;
    for (const v of graph.adjacency[u]) {
      if (disc[v] < 0) {
        dfs(v, u);
        low[u] = Math.min(low[u], low[v]);
        if (low[v] > disc[u]) bridges.add(edgeKey(u, v));
      } else if (v !== parent) {
        low[u] = Math.min(low[u], disc[v]);
      }
    }
  };
  for (let i = 0; i < n; i++) if (disc[i] < 0) dfs(i, -1);

  return graph.edges.filter(edge => {
    const a = graph.index.get(edge.source);
    const b = graph.index.get(edge.target);
    if (a === undefined || b === undefined) return false;
    return bridges.has(edgeKey(a, b));
  });
}

/**
 * 社群发现：模块度贪心（Louvain 局部移动）。
 * 相比标签传播，在含桥（dumbbell）图上不会塔缩成单一社群，结果稳定可解释。
 */
export function communities(graph: GraphModel, maxIterations = 30): Map<string, number> {
  const n = graph.nodes.length;
  if (n === 0) return new Map();

  const adjacency: { to: number; weight: number }[][] = graph.nodes.map(() => []);
  for (const edge of graph.edges) {
    const a = graph.index.get(edge.source);
    const b = graph.index.get(edge.target);
    if (a === undefined || b === undefined || a === b) continue;
    const weight = Math.max(1, edge.strength || 1);
    adjacency[a].push({ to: b, weight });
    adjacency[b].push({ to: a, weight });
  }

  const degree = adjacency.map(list => list.reduce((sum, item) => sum + item.weight, 0));
  const twoM = degree.reduce((sum, value) => sum + value, 0); // = 2m
  const comm = graph.nodes.map((_, i) => i);
  const total = [...degree]; // 各社群内部度之和

  if (twoM === 0) {
    const isolates = new Map<string, number>();
    graph.nodes.forEach((node, i) => isolates.set(node.id, i));
    return isolates;
  }

  for (let iter = 0; iter < maxIterations; iter++) {
    let moved = false;
    for (let i = 0; i < n; i++) {
      const own = comm[i];
      const weightToCommunity = new Map<number, number>();
      for (const { to, weight } of adjacency[i]) {
        const c = comm[to];
        weightToCommunity.set(c, (weightToCommunity.get(c) ?? 0) + weight);
      }

      total[own] -= degree[i];
      let bestCommunity = own;
      let bestGain = (weightToCommunity.get(own) ?? 0) - (total[own] * degree[i]) / twoM;

      for (const [c, weightToC] of [...weightToCommunity.entries()].sort((a, b) => a[0] - b[0])) {
        if (c === own) continue;
        const gain = weightToC - (total[c] * degree[i]) / twoM;
        if (gain > bestGain + 1e-9) {
          bestGain = gain;
          bestCommunity = c;
        }
      }

      comm[i] = bestCommunity;
      total[bestCommunity] += degree[i];
      if (bestCommunity !== own) moved = true;
    }
    if (!moved) break;
  }

  // 归一化为 0..k-1，按社群规模降序编号（大社群在前）
  const sizes = new Map<number, number>();
  for (const c of comm) sizes.set(c, (sizes.get(c) ?? 0) + 1);
  const ordered = [...sizes.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0]).map(([c]) => c);
  const remap = new Map<number, number>();
  ordered.forEach((label, i) => remap.set(label, i));

  const result = new Map<string, number>();
  graph.nodes.forEach((node, i) => result.set(node.id, remap.get(comm[i]) ?? 0));
  return result;
}

/** 最短路径（BFS，无向无权），返回节点 id 序列。 */
export function shortestPath(graph: GraphModel, fromId: string, toId: string): string[] | null {
  const start = graph.index.get(fromId);
  const goal = graph.index.get(toId);
  if (start === undefined || goal === undefined) return null;
  if (start === goal) return [fromId];

  const prev = new Array<number>(graph.nodes.length).fill(-1);
  const seen = new Array<boolean>(graph.nodes.length).fill(false);
  const queue: number[] = [start];
  seen[start] = true;

  while (queue.length) {
    const v = queue.shift() as number;
    if (v === goal) break;
    for (const w of graph.adjacency[v]) {
      if (seen[w]) continue;
      seen[w] = true;
      prev[w] = v;
      queue.push(w);
    }
  }
  if (!seen[goal]) return null;

  const path: number[] = [];
  let cursor = goal;
  while (cursor !== -1) {
    path.push(cursor);
    cursor = prev[cursor];
  }
  return path.reverse().map(i => graph.nodes[i].id);
}

/** 关联矩阵（对称，值为关系强度之和）。 */
export function adjacencyMatrix(graph: GraphModel): { nodes: GNode[]; matrix: number[][] } {
  const n = graph.nodes.length;
  const matrix = Array.from({ length: n }, () => new Array<number>(n).fill(0));
  for (const edge of graph.edges) {
    const a = graph.index.get(edge.source);
    const b = graph.index.get(edge.target);
    if (a === undefined || b === undefined) continue;
    matrix[a][b] += edge.strength;
    matrix[b][a] += edge.strength;
    matrix[a][a] = Math.max(matrix[a][a], 0);
  }
  return { nodes: graph.nodes, matrix };
}

export type CriticalNode = {
  id: string;
  label: string;
  type: string;
  degree: number;
  weighted: number;
  betweenness: number;
  community: number;
  isArticulation: boolean;
  score: number;
};

/** 关键节点排序：度、介数、割点三路加权。 */
export function criticalNodes(graph: GraphModel, limit = 12): CriticalNode[] {
  const degrees = degreeStats(graph);
  const between = betweennessCentrality(graph);
  const comm = communities(graph);
  const cuts = new Set(articulationPoints(graph));

  const maxDegree = Math.max(1, ...[...degrees.values()].map(d => d.degree));
  const maxBetween = Math.max(1, ...[...between.values()]);

  const ranked: CriticalNode[] = graph.nodes.map(node => {
    const d = degrees.get(node.id) ?? { degree: 0, weighted: 0, inDegree: 0, outDegree: 0 };
    const b = between.get(node.id) ?? 0;
    const isCut = cuts.has(node.id);
    const score =
      (d.degree / maxDegree) * 0.45 + (b / maxBetween) * 0.4 + (isCut ? 0.15 : 0);
    return {
      id: node.id,
      label: node.label,
      type: node.type,
      degree: d.degree,
      weighted: d.weighted,
      betweenness: b,
      community: comm.get(node.id) ?? 0,
      isArticulation: isCut,
      score,
    };
  });

  return ranked.sort((a, b) => b.score - a.score).slice(0, limit);
}

export type GraphSummary = {
  nodeCount: number;
  edgeCount: number;
  density: number;
  componentCount: number;
  largestComponent: number;
  isolateCount: number;
  communityCount: number;
  articulationCount: number;
  bridgeCount: number;
  averageDegree: number;
};

export function graphSummary(graph: GraphModel): GraphSummary {
  const n = graph.nodes.length;
  const e = graph.edges.length;
  const degrees = degreeStats(graph);
  const comps = connectedComponents(graph);
  const comm = communities(graph);
  const density = n <= 1 ? 0 : (2 * e) / (n * (n - 1));
  return {
    nodeCount: n,
    edgeCount: e,
    density,
    componentCount: comps.length,
    largestComponent: comps.reduce((max, c) => Math.max(max, c.length), 0),
    isolateCount: [...degrees.values()].filter(d => d.degree === 0).length,
    communityCount: new Set([...comm.values()]).size,
    articulationCount: articulationPoints(graph).length,
    bridgeCount: bridgeEdges(graph).length,
    averageDegree: n === 0 ? 0 : [...degrees.values()].reduce((sum, d) => sum + d.degree, 0) / n,
  };
}

/** 给社群分配稳定色板。 */
export const COMMUNITY_COLORS = [
  '#2563eb',
  '#6d28d9',
  '#0f7a5a',
  '#b3261e',
  '#a16207',
  '#0f766e',
  '#c2410c',
  '#4338ca',
];

export function communityColor(index: number): string {
  return COMMUNITY_COLORS[index % COMMUNITY_COLORS.length];
}